import { useEffect, useState } from 'react';
import axios from 'axios';
import { mensagemDeErro } from '../../utils/Utils';
import { Conta } from './typeConta';
import { useContaContexto } from '../../contextos/ContaContexto';
import useAvisos from '../../hooks/useAviso';
import { usePerfilContexto } from '../../contextos/PerfilContexto';

const API_URL = process.env.REACT_APP_API_URL + '/contas';

export function useContas() {
  const { perfil, setPerfil } = usePerfilContexto();
  const { contas, setContas } = useContaContexto();
  const [loading, setLoading] = useState<boolean>(false);

  const { adicionarAviso } = useAvisos();

  // Função auxiliar para obter o token JWT do localStorage
  const getToken = () => localStorage.getItem('token');

  // Fetch all contas with JWT token
  const fetchContas = async () => {
    if (loading) {
      return;
    }

    const token = getToken();
    if (!token) {
      adicionarAviso('Usuário não autenticado.', 'erro');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get<Conta[]>(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setContas(response.data);
      setPerfil((prev) => ({ ...prev, carregarContas: false }));
    } catch (err) {
      adicionarAviso('Não foi possível carregar as contas.');
    } finally {
      setLoading(false);
    }
  };

  // Create a new conta with JWT token
  const createConta = async (newConta: Conta) => {
    const token = getToken();
    if (!token) {
      adicionarAviso('Usuário não autenticado.', 'erro');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(API_URL, newConta, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      response.data.saldo = newConta.saldo;

      setContas((prevContas) => [...prevContas, response.data]);

      adicionarAviso('Conta cadastrada.', 'sucesso');
    } catch (err) {
      adicionarAviso('Não foi possível cadastrar a conta.');
    } finally {
      setLoading(false);
    }
  };

  // Update a conta with JWT token
  const updateConta = async (id: number, updatedConta: Conta) => {
    const token = getToken();
    if (!token) {
      adicionarAviso('Usuário não autenticado.', 'erro');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API_URL}/${id}`, updatedConta, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setContas((prevContas) => prevContas.map((conta) => (conta.id === id ? { ...response.data, saldo: conta.saldo } : conta)));
      adicionarAviso('Conta atualizada.', 'sucesso');
    } catch (err) {
      adicionarAviso('Não foi possível atualizar a conta.');
    } finally {
      setLoading(false);
    }
  };

  // Delete a conta with JWT token
  const deleteConta = async (listaIds: string[]) => {
    const token = getToken();
    if (!token) {
      adicionarAviso('Usuário não autenticado.', 'erro');
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_URL}/`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: {
          ids: listaIds
        }
      });
      setContas((prevContas) => prevContas.filter((conta) => !listaIds.includes(conta.id?.toString() || '')));
      adicionarAviso('Conta excluída.', 'sucesso');
    } catch (err) {
      const mensagem = mensagemDeErro(err);
      if (mensagem) {
        adicionarAviso(mensagem);
      } else {
        adicionarAviso('Não foi possível excluir a conta.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (perfil.carregarContas) {
      fetchContas();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfil.carregarContas]);

  return {
    contas,
    loading,
    fetchContas,
    createConta,
    updateConta,
    deleteConta
  };
}

